import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const noticeMessage = location.state?.message || '';
  const redirectTo = location.state?.redirectTo || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (!result.user.emailVerified) {
        navigate('/verify-email');
        return;
      }
      navigate(redirectTo);
    } catch (error) {
      console.error('ログインエラー:', error);
      
      // Firebase Authエラーコードに基づいて具体的なメッセージを表示
      switch (error.code) {
        case 'auth/user-not-found':
          setError('このメールアドレスは登録されていません。新規登録をお願いします。');
          break;
        case 'auth/wrong-password':
          setError('パスワードが正しくありません。メールアドレスとパスワードをご確認ください。');
          break;
        case 'auth/invalid-email':
          setError('メールアドレスの形式が正しくありません。example@domain.com の形式で入力してください。');
          break;
        case 'auth/user-disabled':
          setError('このアカウントは無効化されています。管理者にお問い合わせください。');
          break;
        case 'auth/too-many-requests':
          setError('ログイン試行回数が多すぎます。しばらく時間を置いてから再度お試しください。');
          break;
        case 'auth/network-request-failed':
          setError('ネットワーク接続に問題があります。インターネット接続を確認してください。');
          break;
        case 'auth/internal-error':
          setError('サーバーでエラーが発生しました。しばらく時間を置いてから再度お試しください。');
          break;
        default:
          setError('ログインに失敗しました。メールアドレスとパスワードを確認し、ネットワーク接続をご確認ください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      
      // ユーザーが既に存在するか確認
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (userDoc.exists()) {
        // 既存ユーザーは通常通りログイン
        navigate(redirectTo);
      } else {
        // 新規ユーザーは追加情報入力画面へ
        navigate(`/google-signup?email=${encodeURIComponent(result.user.email)}&displayName=${encodeURIComponent(result.user.displayName || '')}`);
      }
    } catch (error) {
      console.error('Googleログインエラー:', error);
      
      // Googleログインのエラーハンドリング
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError('Googleログインがキャンセルされました。');
          break;
        case 'auth/popup-blocked':
          setError('ポップアップがブロックされました。ブラウザのポップアップ設定を確認してください。');
          break;
        case 'auth/cancelled-popup-request':
          setError('Googleログインがキャンセルされました。');
          break;
        case 'auth/network-request-failed':
          setError('ネットワーク接続に問題があります。インターネット接続を確認してください。');
          break;
        case 'auth/too-many-requests':
          setError('リクエスト回数が多すぎます。しばらく時間を置いてから再度お試しください。');
          break;
        default:
          setError('Googleログインに失敗しました。しばらく時間を置いてから再度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="card auth-card">
        <h2 className="text-center mb-4">ログイン</h2>
        
        {noticeMessage && <div className="alert alert-warning">{noticeMessage}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">メールアドレス</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="example@domain.com"
              autoComplete="email"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">パスワード</label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="パスワードを入力"
              autoComplete="current-password"
            />
          </div>
          
          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading}
          >
            {loading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>

        <div className="divider">
          <span>または</span>
        </div>

        <button
          className="btn btn-google w-100"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <span className="google-icon">🔗</span>
          {loading ? 'ログイン中...' : 'Googleでログイン'}
        </button>

        <div className="auth-links mt-4">
          <div className="text-center">
            <Link to="/signup">新規登録はこちら</Link>
          </div>
          <div className="text-center mt-2">
            <Link to="/reset-password">パスワードを忘れた場合</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
